import { InfoCircleFilled } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { LegacyPopover } from '@creditornot/cb-popover';
import { Tag } from '@creditornot/cb-tag';
import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import { ConfirmModal } from 'common/components';
import ErrorToastContent from 'common/components/ErrorToastContent';
import LocalizedNumber from 'common/components/LocalizedNumber';
import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { Typography as T } from 'common/typography';
import { localize } from 'common/utils';
import { useCountryConfigContext } from 'modules/country/CountryConfigContext';
import { deleteOptionChoice, updateOption } from 'modules/shared-menu/api';
import { useOfferingMenuFeaturesContext } from 'modules/shared-menu/contexts/OfferingMenuFeaturesContext';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';
import { useSelectionInfoContext } from 'modules/shared-menu/contexts/SelectionInfoContext';

import { OptionChoiceActions } from './OptionChoiceActions';
import {
  COUNT_COLUMN_WIDTH,
  DEFAULT_VALUE_COLUMN_WIDTH,
  DEPOSIT_COLUMN_WIDTH,
  DISABLED_COLUMN_WIDTH,
  HIDE_ON_IPAD_COLUMN_WIDTH,
  NAME_COLUMN_MIN_WIDTH,
} from './constants';
import { buildDisableOptionChoicePayload, buildEnableOptionChoicePayload } from './utils';
import { useSelectionData } from '../../../../hooks/useSelectionData';
import { useDeposits } from '../../hooks/useDeposits';
import { Offer, Option, OptionValue } from '../../types';
import { getOffersThatWillBecomeUnpurchasable } from '../../unpurchasabilityUtils';
import { UnpurchasableOfferWarning } from '../OptionForm/UnpurchasableOfferWarning';

type Props = {
  language: Language;
  optionId: string;
  optionChoice: OptionValue;
  isDefault: boolean;
  isChoice: boolean;
  isMultiChoice: boolean;
};

const FirstFieldGroup = styled.div`
  flex: 1 1 auto;
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
`;

const SecondFieldGroup = styled.div`
  flex: 0 1 auto;
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
`;

const FieldContainer = styled.div`
  display: flex;
  flex-flow: column;
  align-items: flex-start;
  justify-content: flex-start;
  margin: 4px 16px 4px 0;
`;

const Root = styled.div`
  display: flex;
  flex: 1 1 auto;
  justify-content: flex-start;
  align-items: center;
  background-color: ${cbSemanticCssVars.surfaceMain};
  border: 1px solid ${cbSemanticCssVars.borderPrimary};
  border-radius: 8px;
  padding: 8px 16px;
  margin-block-start: 8px;

  & + & {
    margin-block-end: 8px;
  }
`;

const Container = styled.div<{ busy: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  flex-wrap: wrap;

  ${props =>
    props.busy &&
    css`
      opacity: 0.5;
    `}
`;

const FieldLabel = styled(T.Small)`
  margin-block-end: 8px;
  width: 100%;
  display: flex;
  align-items: center;
  color: ${cbSemanticCssVars.textPrimary};
`;

const OptionValuePosId = styled(T.Body3)`
  flex: 0 0 auto;
  hyphens: auto;
`;

const OptionValueDeposit = styled(T.Body3)`
  flex: 0 0 auto;
  width: ${DEPOSIT_COLUMN_WIDTH}px;
  hyphens: auto;
`;

const OptionValueName = styled(T.Body3)`
  flex: 1 1 auto;
  min-width: ${NAME_COLUMN_MIN_WIDTH}px;
  hyphens: auto;
`;

const OptionValueCount = styled(T.Body3)`
  flex: 0 0 auto;
  width: ${COUNT_COLUMN_WIDTH}px;
  hyphens: auto;
`;

const OptionValueHideOnIPad = styled(T.Body3)`
  flex: 0 0 auto;
  width: ${HIDE_ON_IPAD_COLUMN_WIDTH}px;
  text-align: center;
  hyphens: auto;
`;

const OptionValueDisabled = styled(T.Body3)`
  flex: 0 0 auto;
  width: ${DISABLED_COLUMN_WIDTH}px;
  hyphens: auto;
`;

const OptionValuePrice = styled(T.Body3)`
  position: relative;
  flex: 0 0 auto;
  width: 70px;
  hyphens: auto;
`;

const OptionValueDefault = styled(T.Body3)`
  flex: 0 0 auto;
  width: ${DEFAULT_VALUE_COLUMN_WIDTH}px;
  display: flex;
  hyphens: auto;
`;

const StyledToolTip = styled(LegacyPopover)`
  width: 12px;
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  margin-inline-start: 4px;
`;

const InfoToolTipIcon = styled(InfoCircleFilled)`
  width: 100%;
  height: auto;
  fill: ${cbSemanticCssVars.textPrimary};
`;

const OptionValueVat = styled(T.Body3)`
  position: relative;
  flex: 0 0 auto;
  display: flex;
  width: 60px;
  hyphens: auto;
`;

const PopoverContent = styled(T.Body3)`
  display: flex;
  padding: 8px;
  max-width: 300px;
`;

const DisabledTag = styled(Tag).attrs({ size: 'tiny', variant: 'red' })``;

export const OptionChoiceView = ({ language, optionId, optionChoice, isDefault, isChoice, isMultiChoice }: Props) => {
  const { offers: allOffers, options: allOptions, selection, updateSelectionData } = useSelectionData();
  const { currency } = useCountryConfigContext();
  const { vlmeConfig } = useSelectionInfoContext();
  const isSharedVenue = !!vlmeConfig?.isSharedVenue;
  const menuPermissions = useOfferingMenuPermissionsContext();
  const menuFeatures = useOfferingMenuFeaturesContext();

  const option = useMemo(() => allOptions.find(o => o.id === optionId), [allOptions, optionId]);

  if (!option) {
    throw Error(`Option with ID ${optionId} not found`);
  }

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [unpurchasableOffers, setUnpurchasableOffers] = useState<Offer[] | null>(null);

  const { data: depositOptions } = useDeposits({ enabled: menuFeatures.bottleDeposits });

  const deposit = depositOptions.find(option => option.value === optionChoice.deposit_permutation_id);
  const depositLabel = deposit ? deposit.label : '-';

  const { mutate: executeDeleteOptionChoice, isLoading: isDeletingOptionChoice } = useMutation(
    () => deleteOptionChoice(selection.id, optionId, optionChoice.id),
    {
      onSuccess: (updatedOption: Option) => {
        updateSelectionData({ actionType: 'updateOption', payload: updatedOption });
        toast.success(<FormattedMessage id="toast.success" />);
      },
      onError: (error: any) => {
        toast.error(<ErrorToastContent error={error} />);
      },
    },
  );

  const { mutate: executeEnableOptionChoice, isLoading: isEnablingOptionChoice } = useMutation(
    () => {
      const payload = buildEnableOptionChoicePayload(option, optionChoice);

      return updateOption(selection.id, option.id, payload);
    },
    {
      onSuccess: (updatedOption: Option) => {
        updateSelectionData({ actionType: 'updateOption', payload: updatedOption });
        toast.success(<FormattedMessage id="toast.success" />);
        setUnpurchasableOffers(null);
      },
      onError: (error: any) => {
        toast.error(<ErrorToastContent error={error} />);
      },
    },
  );

  const { mutate: executeDisableOptionChoice, isLoading: isDisablingOptionChoice } = useMutation(
    () => {
      const payload = buildDisableOptionChoicePayload(option, optionChoice);

      return updateOption(selection.id, option.id, payload);
    },
    {
      onSuccess: (updatedOption: Option) => {
        updateSelectionData({ actionType: 'updateOption', payload: updatedOption });
        toast.success(<FormattedMessage id="toast.success" />);
        setUnpurchasableOffers(null);
      },
      onError: (error: any) => {
        toast.error(<ErrorToastContent error={error} />);
      },
    },
  );

  const handleClickDisable = useCallback(() => {
    const optionWithCurrentChoiceDisabled = {
      ...option,
      values: option.values.map(choice =>
        choice.id === optionChoice.id
          ? { ...choice, enabled: { ...choice.enabled, enabled: !choice.enabled.enabled } }
          : choice,
      ),
    };

    const offersThatWillBecomeUnpurchasable = getOffersThatWillBecomeUnpurchasable(
      optionWithCurrentChoiceDisabled,
      allOffers,
      allOptions,
    );

    if (offersThatWillBecomeUnpurchasable && offersThatWillBecomeUnpurchasable.length > 0) {
      setUnpurchasableOffers(offersThatWillBecomeUnpurchasable);
    } else {
      executeDisableOptionChoice();
    }
  }, [allOffers, allOptions, executeDisableOptionChoice, option, optionChoice]);

  const handleClickDelete = useCallback(() => {
    const optionWithCurrentChoiceRemoved = {
      ...option,
      values: option.values.filter(choice => choice.id !== optionChoice.id),
    };

    const offersThatWillBecomeUnpurchasable = getOffersThatWillBecomeUnpurchasable(
      optionWithCurrentChoiceRemoved,
      allOffers,
      allOptions,
    );

    if (offersThatWillBecomeUnpurchasable && offersThatWillBecomeUnpurchasable.length > 0) {
      setUnpurchasableOffers(offersThatWillBecomeUnpurchasable);
    }
    setIsDeleteModalOpen(true);
  }, [allOffers, allOptions, option, optionChoice]);

  return (
    <Root>
      <Container busy={isDeletingOptionChoice || isEnablingOptionChoice || isDisablingOptionChoice}>
        <FirstFieldGroup>
          <FieldContainer style={{ flex: '1 1 auto' }}>
            <FieldLabel>
              <FormattedMessage id="menu-option.name-heading" />
            </FieldLabel>
            <OptionValueName>{localize(optionChoice.name, language)}</OptionValueName>
          </FieldContainer>
          {(isChoice || isMultiChoice) && (
            <FieldContainer>
              <FieldLabel>
                <FormattedMessage id="menu-option.disabled" />
              </FieldLabel>
              <OptionValueDisabled>
                {!optionChoice.enabled.enabled ? (
                  <DisabledTag>
                    <FormattedMessage id="menu-option.disabled" />
                  </DisabledTag>
                ) : (
                  '-'
                )}
              </OptionValueDisabled>
            </FieldContainer>
          )}
          <FieldContainer>
            <FieldLabel>
              <FormattedMessage id="menu-item.deposit" />
              <StyledToolTip
                content={
                  <PopoverContent>
                    <FormattedMessage id="menu-item.deposit-tooltip" />
                  </PopoverContent>
                }
                disableContentPadding
              >
                <InfoToolTipIcon name="info" />
              </StyledToolTip>
            </FieldLabel>
            <OptionValueDeposit>{depositLabel}</OptionValueDeposit>
          </FieldContainer>
        </FirstFieldGroup>
        <SecondFieldGroup>
          <FieldContainer>
            <FieldLabel>
              <FormattedMessage id="menu-option.vat-header" />
              <StyledToolTip
                contentZIndex={100002} // Ensure tooltip is shown over OptionModal
                content={
                  <PopoverContent>
                    <FormattedMessage id="menu-option.vat-tooltip" />
                  </PopoverContent>
                }
                disableContentPadding
              >
                <InfoToolTipIcon name="info" />
              </StyledToolTip>
            </FieldLabel>
            <OptionValueVat>
              {optionChoice.vat_percentage !== undefined ? (
                <LocalizedNumber
                  value={optionChoice.vat_percentage / 100}
                  options={{
                    style: 'percent',
                    maximumFractionDigits: 2,
                  }}
                />
              ) : (
                '-'
              )}
            </OptionValueVat>
          </FieldContainer>
          <FieldContainer>
            <FieldLabel>
              <FormattedMessage id="menu-option.price-heading" />
            </FieldLabel>
            <OptionValuePrice>
              <MonetaryAmount amount={optionChoice.price} currency={currency} />
            </OptionValuePrice>
          </FieldContainer>
          <FieldContainer>
            <FieldLabel style={{ justifyContent: 'flex-end' }}>
              <FormattedMessage id="menu.pos-id" />
            </FieldLabel>
            <OptionValuePosId>{optionChoice.external_id || '-'}</OptionValuePosId>
          </FieldContainer>
          {!optionChoice.multi_choice_config && (
            <FieldContainer>
              <FieldLabel>
                <FormattedMessage id="menu-option.hide-on-ipad" />
              </FieldLabel>
              <OptionValueHideOnIPad>{!optionChoice.visible_to_merchant_app && '✕'}</OptionValueHideOnIPad>
            </FieldContainer>
          )}
          {optionChoice.multi_choice_config && (
            <>
              <FieldContainer>
                <FieldLabel>
                  <FormattedMessage id="menu-option.mincount-heading" />
                </FieldLabel>
                <OptionValueCount>{optionChoice.multi_choice_config.selection_range.min}</OptionValueCount>
              </FieldContainer>
              <FieldContainer>
                <FieldLabel>
                  <FormattedMessage id="menu-option.maxcount-heading" />
                  <StyledToolTip
                    contentZIndex={100002} // Ensure tooltip is shown over OptionModal
                    content={
                      <PopoverContent>
                        <FormattedMessage id="menu-option.maxcount-description" />
                      </PopoverContent>
                    }
                    disableContentPadding
                  >
                    <InfoToolTipIcon name="info" />
                  </StyledToolTip>
                </FieldLabel>
                <OptionValueCount>{optionChoice.multi_choice_config.selection_range.max || '-'}</OptionValueCount>
              </FieldContainer>
            </>
          )}
          <FieldContainer>
            <FieldLabel>
              <FormattedMessage id="menu-option.default-option-heading" />
            </FieldLabel>
            <OptionValueDefault>{isDefault ? '✕' : '-'}</OptionValueDefault>
          </FieldContainer>
        </SecondFieldGroup>
      </Container>
      {(isChoice || isMultiChoice) && !isSharedVenue && menuPermissions.contentEdit && (
        <OptionChoiceActions
          isDefaultChoice={isDefault}
          onDelete={handleClickDelete}
          onDisable={handleClickDisable}
          onEnable={() => executeEnableOptionChoice()}
          optionChoice={optionChoice}
        />
      )}
      {isDeleteModalOpen && (
        <ConfirmModal
          cancelText={<FormattedMessage id="state.cancel" />}
          confirmText={<FormattedMessage id="state.confirm" />}
          header={<FormattedMessage id="menu-option.option-choice-actions.delete" />}
          loading={isDeletingOptionChoice}
          onCancel={() => {
            setIsDeleteModalOpen(false);
            setUnpurchasableOffers(null);
          }}
          onConfirm={() => executeDeleteOptionChoice()}
          open
          description={
            unpurchasableOffers ? (
              <UnpurchasableOfferWarning
                isMultiChoice={isMultiChoice}
                offers={unpurchasableOffers}
                language={language}
              />
            ) : undefined
          }
        />
      )}
      {unpurchasableOffers && unpurchasableOffers.length > 0 && !isDeleteModalOpen && (
        <ConfirmModal
          cancelText={<FormattedMessage id="state.cancel" />}
          confirmText={<FormattedMessage id="state.confirm" />}
          header={<FormattedMessage id="menu-option.option-choice-actions.disable" />}
          loading={isDisablingOptionChoice}
          onCancel={() => setUnpurchasableOffers(null)}
          onConfirm={() => executeDisableOptionChoice()}
          description={
            <UnpurchasableOfferWarning isMultiChoice={isMultiChoice} offers={unpurchasableOffers} language={language} />
          }
          open
        />
      )}
    </Root>
  );
};
